import { api } from './api';
import { getAccessToken } from './auth';
import i18n from '../i18n';
import type {
  AuditReportPayload,
  StatusReportPayload,
  StoredReport,
  ReportKind,
  ReportFormat,
  ReportTemplate,
} from '@traaker/shared';

export interface StatusReportInput {
  brandId: string;
  windowStart?: string;
  windowEnd?: string;
  focusTaskIds?: string[];
  language?: 'fr' | 'en';
}

/**
 * Fetch the current tenant's agency logo as a base64 data URL. The endpoint
 * is auth-protected so a plain <img src> can't reach it — we pull the bytes
 * with a bearer token and inline them. Used by both on-page preview and the
 * PDF/PPTX exporters (react-pdf's Image + pptxgenjs.addImage both accept
 * data URLs but not auth'd remote URLs).
 *
 * Returns null when no logo is uploaded — callers should fall back to a
 * text-only header.
 */
export async function fetchAgencyLogoDataUrl(): Promise<string | null> {
  const token = await getAccessToken();
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
  try {
    const res = await fetch(`${base}/tenants/agency-branding/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Blob → base64 (no data-URL prefix) for the report-library upload body. */
async function blobToBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
  return dataUrl.slice(dataUrl.indexOf(',') + 1); // strip "data:...;base64,"
}

export const reportsApi = {
  audit: (brandId: string, language?: 'fr' | 'en') =>
    api.get<AuditReportPayload>(
      `/reports/audit?brandId=${encodeURIComponent(brandId)}` +
        (language ? `&language=${language}` : ''),
    ),

  status: (input: StatusReportInput) =>
    api.post<StatusReportPayload>('/reports/status', input),

  // ── Per-tenant report template (section config) ──

  getTemplate: () =>
    api.get<{ template: ReportTemplate; sections: { audit: string[]; status: string[] } }>(
      '/reports/template',
    ),

  saveTemplate: (template: { audit?: Record<string, boolean>; status?: Record<string, boolean> }) =>
    api.patch<{ template: ReportTemplate }>('/reports/template', template),

  // ── Report library (generated artifacts) ──

  listLibrary: (brandId?: string) =>
    api.get<{ reports: StoredReport[] }>(
      `/reports/library${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`,
    ),

  /** Upload a browser-rendered report blob → stores it + bills generation (agency). */
  async generate(input: {
    brandId: string;
    brandName?: string;
    kind: ReportKind;
    format: ReportFormat;
    name: string;
    blob: Blob;
  }): Promise<{ report: StoredReport }> {
    const dataBase64 = await blobToBase64(input.blob);
    return api.post<{ report: StoredReport }>('/reports/library', {
      brandId: input.brandId,
      brandName: input.brandName,
      kind: input.kind,
      format: input.format,
      name: input.name,
      dataBase64,
    });
  },

  rename: (reportId: string, name: string) =>
    api.patch<{ report: StoredReport }>(`/reports/library/${reportId}`, { name }),

  remove: (reportId: string) => api.delete(`/reports/library/${reportId}`),

  /** Mint (or rotate) a public 30-day share link. Returns the full URL. */
  share: (reportId: string) =>
    api.post<{ url: string; expiresAt: string }>(`/reports/library/${reportId}/share`, {}),

  /** Revoke a report's share link (URL stops resolving immediately). */
  revokeShare: (reportId: string) => api.delete(`/reports/library/${reportId}/share`),

  /** Download a stored report (free) — authed fetch → save. */
  async download(reportId: string, name: string, format: ReportFormat): Promise<void> {
    const token = await getAccessToken();
    const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
    const res = await fetch(`${base}/reports/library/${reportId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(i18n.t('reportsService.downloadFailed'));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
